import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_PATH } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Coins, LogOut, Plus, Download, Trophy, KeyRound, Menu, X, Banknote, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";
import { DateTimePicker } from "@/components/DateTimePicker";

function fmt(x) { return Number(x || 0).toFixed(2); }
function fmtDT(s) { if (!s) return "—"; try { return new Date(s).toLocaleString(); } catch { return s; } }

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [rules, setRules] = useState("");
  const [brand, setBrand] = useState({ brand_name: "", brand_logo_url: "" });
  const [announcements, setAnnouncements] = useState([]);
  const [evForm, setEvForm] = useState({ team1: "", team2: "", odds: 1.95, start_time: "", end_time: "" });
  const [userForm, setUserForm] = useState({ username: "", password: "", mobile: "" });
  const [annForm, setAnnForm] = useState({ title: "", message: "", start_time: "", end_time: "" });
  const [loginCode, setLoginCode] = useState({ code: "", rotated_at: "", visible: false });
  const [viewBets, setViewBets] = useState(null); // {event, bets}
  const [editEvent, setEditEvent] = useState(null); // {id, team1, team2, odds, start_time, end_time, void_bets}
  const [declarePreview, setDeclarePreview] = useState(null); // {id, winner, winners, losers, voids, admin_pnl, count}
  const [navOpen, setNavOpen] = useState(false);
  const [eventSubmitting, setEventSubmitting] = useState(false);
  const [declareSubmitting, setDeclareSubmitting] = useState(false);
  const [pnlData, setPnlData] = useState(null); // { total_pnl, total_count, events }
  const [pnlListOpen, setPnlListOpen] = useState(true);
  const [settledData, setSettledData] = useState(null);
  const [settledOpen, setSettledOpen] = useState({}); // id -> bool
  const loadSettled = async () => {
    try { const { data } = await api.get("/admin/settled-events"); setSettledData(data); }
    catch (err) { toast.error(formatError(err)); }
  };
  const loadPnl = async () => {
    try { const { data } = await api.get("/admin/pnl"); setPnlData(data); }
    catch (err) { toast.error(formatError(err)); }
  };
  const [withdrawals, setWithdrawals] = useState([]);
  const [resolveModal, setResolveModal] = useState(null); // { id, action:'approve'|'reject', username, amount, comment }
  const closeNav = () => setNavOpen(false);

  const loadAll = async () => {
    try {
      const [e, u, r, b, a, c, w] = await Promise.all([
        api.get("/admin/events"),
        api.get("/admin/users"),
        api.get("/admin/rules"),
        api.get("/admin/brand"),
        api.get("/admin/announcements"),
        api.get("/admin/login-code"),
        api.get("/admin/withdrawals"),
      ]);
      setEvents(e.data); setUsers(u.data); setRules(r.data.rules || "");
      setBrand({ brand_name: b.data.brand_name || "", brand_logo_url: b.data.brand_logo_url || "", support_whatsapp: b.data.support_whatsapp || "", support_telegram: b.data.support_telegram || "" });
      setAnnouncements(a.data);
      setLoginCode((s) => ({ ...s, code: c.data.code, rotated_at: c.data.rotated_at }));
      setWithdrawals(w.data);
      // Fire and forget: All-time P&L for the header tile (small aggregate, non-blocking).
      loadPnl();
    } catch (err) { toast.error(formatError(err)); }
  };

  // Narrow refetchers to avoid hitting all endpoints after every mutation
  const refetchEvents = async () => {
    try { const { data } = await api.get("/admin/events"); setEvents(data); } catch (_e) { /* silent */ }
  };
  const refetchUsers = async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data); } catch (_e) { /* silent */ }
  };
  const refetchAnnouncements = async () => {
    try { const { data } = await api.get("/admin/announcements"); setAnnouncements(data); } catch (_e) { /* silent */ }
  };
  const refetchWithdrawals = async () => {
    try { const { data } = await api.get("/admin/withdrawals"); setWithdrawals(data); } catch (_e) { /* silent */ }
  };
  useEffect(() => { loadAll(); }, []);

  // Auto-logout the admin the moment their scheduled active_until passes.
  useEffect(() => {
    if (!user?.active_until) return;
    const check = () => {
      if (new Date(user.active_until).getTime() <= Date.now()) {
        try { toast.error("Your admin account has expired. Please contact the super admin."); } catch (_e) { /* noop */ }
        logout();
        nav(ADMIN_PATH, { replace: true });
      }
    };
    check();
    const id = setInterval(check, 30_000); // check every 30s
    return () => clearInterval(id);
  }, [user?.active_until, logout, nav]);

  const rotateCode = async () => {
    try {
      const { data } = await api.post("/admin/login-code/rotate");
      setLoginCode({ code: data.code, rotated_at: data.rotated_at, visible: true });
      toast.success(`New code: ${data.code}`);
    } catch (err) { toast.error(formatError(err)); }
  };

  const createEvent = async (e) => {
    e.preventDefault();
    if (eventSubmitting) return;
    if (!evForm.team1 || !evForm.team2) { toast.error("Both team names are required"); return; }
    if (!evForm.start_time || !evForm.end_time) { toast.error("Start and cutoff time are required"); return; }
    setEventSubmitting(true);
    try {
      await api.post("/admin/events", {
        team1: evForm.team1, team2: evForm.team2, odds: parseFloat(evForm.odds),
        start_time: new Date(evForm.start_time).toISOString(),
        end_time: new Date(evForm.end_time).toISOString(),
      });
      toast.success("Event created");
      setEvForm({ team1:"", team2:"", odds:1.95, start_time:"", end_time:"" });
      // New event only affects the events list.
      refetchEvents();
    } catch (err) { toast.error(formatError(err)); }
    finally { setEventSubmitting(false); }
  };

  const declare = async (id, winner) => {
    try {
      const ev = events.find(e => e.id === id);
      const { data } = await api.get(`/admin/events/${id}/settlement-preview`, { params: { winner } });
      setDeclarePreview({ id, winner, team1: ev?.team1, team2: ev?.team2, ...data });
    } catch (err) { toast.error(formatError(err)); }
  };
  const confirmDeclare = async () => {
    if (!declarePreview || declareSubmitting) return;
    setDeclareSubmitting(true);
    try {
      const r = await api.post(`/admin/events/${declarePreview.id}/declare-result`, { winner: declarePreview.winner });
      toast.success(`Settled. P&L ${fmt(r.data.admin_pnl)}`);
      // Settle affects: events (status→settled), history (new row), users (winners' balances credited).
      setDeclarePreview(null);
      setPnlData(null); // invalidate P&L cache; loadPnl below refreshes header tile too
      setSettledData(null); // invalidate settled events cache
      Promise.all([refetchEvents(), refetchUsers(), loadPnl()]);
    } catch (err) { toast.error(formatError(err)); }
    finally { setDeclareSubmitting(false); }
  };

  const openViewBets = async (ev) => {
    try {
      const { data } = await api.get(`/admin/events/${ev.id}/bets`);
      setViewBets({ event: data.event, bets: data.bets });
    } catch (err) { toast.error(formatError(err)); }
  };

  const openEdit = (ev) => {
    // DateTimePicker uses local wall-clock strings (YYYY-MM-DDTHH:mm, no timezone).
    // Convert the ISO UTC value we got from the server into the admin's local wall-clock.
    const toLocalInput = (d) => {
      try {
        const dt = new Date(d);
        const pad = (n) => String(n).padStart(2, "0");
        return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
      } catch { return ""; }
    };
    setEditEvent({
      id: ev.id, team1: ev.team1, team2: ev.team2, odds: ev.odds,
      start_time: toLocalInput(ev.start_time), end_time: toLocalInput(ev.end_time), void_bets: false,
    });
  };
  const saveEdit = async () => {
    if (!editEvent) return;
    const body = {
      team1: editEvent.team1, team2: editEvent.team2, odds: parseFloat(editEvent.odds),
      start_time: new Date(editEvent.start_time).toISOString(),
      end_time: new Date(editEvent.end_time).toISOString(),
      void_bets: !!editEvent.void_bets,
    };
    try {
      await api.put(`/admin/events/${editEvent.id}`, body);
      toast.success(editEvent.void_bets ? "Event updated, bets voided & refunded" : "Event updated");
      setEditEvent(null);
      // Event edit always affects events list. If void_bets was ticked, user balances also refund.
      const tasks = [refetchEvents()];
      if (editEvent.void_bets) tasks.push(refetchUsers());
      Promise.all(tasks);
    }
    catch (err) { toast.error(formatError(err)); }
  };

  const exportBets = async (id) => {
    try {
      const token = localStorage.getItem("tk_token");
      const res = await fetch(`${API}/admin/events/${id}/export`, { headers: { Authorization: `Bearer ${token}` }});
      const txt = await res.text();
      const blob = new Blob([txt], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `event_${id}_bets.txt`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/users", userForm); toast.success("User created"); setUserForm({ username:"", password:"", mobile:"" }); refetchUsers(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const [adjustModal, setAdjustModal] = useState(null); // { user, direction: 'credit'|'debit', amount, comment }
  const [bulkModal, setBulkModal] = useState(null); // { direction, amount, comment }
  const [delUser, setDelUser] = useState(null);
  const [delUserConfirm, setDelUserConfirm] = useState("");
  const [userSearch, setUserSearch] = useState("");

  const deleteUser = async () => {
    if (!delUser) return;
    if (delUserConfirm.trim() !== delUser.username) { toast.error("Username does not match"); return; }
    try {
      await api.delete(`/admin/users/${delUser.id}`);
      toast.success(`User '${delUser.username}' deleted`);
      setDelUser(null); setDelUserConfirm("");
      // Deleting a user cascades their bets/tx/withdrawals on the backend → refresh users + withdrawals.
      Promise.all([refetchUsers(), refetchWithdrawals()]);
    } catch (err) { toast.error(formatError(err)); }
  };

  const openAdjust = (u) => setAdjustModal({ user: u, direction: "credit", amount: "", comment: "" });
  const openBulk = () => setBulkModal({ direction: "credit", amount: "", comment: "" });

  const submitAdjust = async () => {
    if (!adjustModal) return;
    const amt = parseFloat(adjustModal.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid positive amount"); return; }
    if (!adjustModal.comment.trim()) { toast.error("Comment is required"); return; }
    const signed = adjustModal.direction === "credit" ? amt : -amt;
    try {
      await api.post("/admin/users/adjust-coins", { user_id: adjustModal.user.id, amount: signed, comment: adjustModal.comment.trim() });
      toast.success(`${adjustModal.direction === "credit" ? "Credited" : "Debited"} ${amt} coins`);
      setAdjustModal(null);
      // Adjust changes a single user's balance → only users list needs refresh.
      refetchUsers();
    } catch (err) { toast.error(formatError(err)); }
  };

  const adjustCoins = (u) => openAdjust(u);

  const submitBulk = async () => {
    if (!bulkModal) return;
    const amt = parseFloat(bulkModal.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid positive amount"); return; }
    if (!bulkModal.comment.trim()) { toast.error("Comment is required"); return; }
    const signed = bulkModal.direction === "credit" ? amt : -amt;
    const verb = bulkModal.direction === "credit" ? "credit" : "debit";
    if (!window.confirm(`Confirm: ${verb} ${amt} coins to/from ALL ${users.length} users?`)) return;
    try {
      const { data } = await api.post("/admin/users/bulk-adjust-coins", { amount: signed, comment: bulkModal.comment.trim() });
      toast.success(`Bulk ${verb} done · ${data.affected} affected · ${data.skipped} skipped`);
      setBulkModal(null);
      // Bulk only changes user balances → users list.
      refetchUsers();
    } catch (err) { toast.error(formatError(err)); }
  };

  const resetUserPw = async (u) => {
    const pw = window.prompt(`New password for ${u.username}:`);
    if (!pw) return;
    try { await api.post(`/admin/users/${u.id}/reset-password`, { new_password: pw }); toast.success("Password reset"); }
    catch (err) { toast.error(formatError(err)); }
  };

  const saveRules = async () => {
    try { await api.put("/admin/rules", { rules }); toast.success("Rules saved"); }
    catch (err) { toast.error(formatError(err)); }
  };

  const saveBrand = async () => {
    try { await api.put("/admin/brand", brand); toast.success("Brand updated"); }
    catch (err) { toast.error(formatError(err)); }
  };

  const createAnnouncement = async (e) => {
    e.preventDefault();
    if (!annForm.start_time || !annForm.end_time) { toast.error("Start and end time are required"); return; }
    try {
      await api.post("/admin/announcements", {
        title: annForm.title, message: annForm.message,
        start_time: new Date(annForm.start_time).toISOString(),
        end_time: new Date(annForm.end_time).toISOString(),
      });
      toast.success("Announcement posted"); setAnnForm({ title:"", message:"", start_time:"", end_time:"" });
      // Only announcements list changes.
      refetchAnnouncements();
    } catch (err) { toast.error(formatError(err)); }
  };
  const delAnnouncement = async (id) => { await api.delete(`/admin/announcements/${id}`); refetchAnnouncements(); };

  const [detailUser, setDetailUser] = useState(null);
  const openUserDetail = async (u) => {
    try { const { data } = await api.get(`/admin/users/${u.id}/details`); setDetailUser(data); }
    catch (e) { toast.error(formatError(e)); }
  };

  const resolveWithdrawal = async (action) => {
    if (!resolveModal) return;
    const { id, comment } = resolveModal;
    if (!action) return;
    if (!comment || !comment.trim()) {
      toast.error(`Please add a comment before ${action === "approve" ? "approving" : "rejecting"}`);
      return;
    }
    try {
      await api.post(`/admin/withdrawals/${id}/${action}`, { comment: comment.trim() });
      toast.success(`Withdrawal ${action === "approve" ? "approved" : "rejected"}`);
      setResolveModal(null);
      // Resolving affects: withdrawals (status) and users (locked/balance changed for that user).
      Promise.all([refetchWithdrawals(), refetchUsers()]);
    } catch (err) { toast.error(formatError(err)); }
  };

  const doLogout = () => { logout(); nav(ADMIN_PATH); };
  const userLoginLink = `${window.location.origin}/login`;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 z-40 bg-background">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={()=>setNavOpen(v=>!v)} data-testid="nav-toggle-btn" className="btn-push">
              {navOpen ? <X className="h-5 w-5"/> : <Menu className="h-5 w-5"/>}
            </Button>
            {user?.brand_logo_url ? <img src={user.brand_logo_url} className="h-8 w-8 rounded" alt="logo" /> : <div className="h-8 w-8 rounded-lg bg-primary grid place-items-center"><Coins className="h-5 w-5 text-primary-foreground" /></div>}
            <div>
              <div className="font-display font-bold">{user?.brand_name || "Admin"} · Console</div>
              <div className="text-xs text-muted-foreground mono hidden sm:block">{userLoginLink}</div>
              {user?.active_until && (
                <div className="text-[11px] mono text-amber-500 mt-0.5" data-testid="active-until-banner">
                  Active until {fmtDT(user.active_until)}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right hidden md:block">
              <div className="text-xs text-muted-foreground">All-time P&L</div>
              <div className={`font-display font-bold text-lg mono ${(pnlData?.total_pnl ?? 0)>=0?"text-green-500":"text-red-500"}`} data-testid="header-pnl">{fmt(pnlData?.total_pnl ?? 0)}</div>
            </div>
            <ThemeToggle />
            <Button variant="outline" onClick={doLogout} data-testid="logout-btn"><LogOut className="h-4 w-4 mr-2" />Logout</Button>
          </div>
        </div>
      </header>

      <main>
        {navOpen && <div className="fixed inset-0 bg-black/50 z-30" onClick={closeNav} data-testid="nav-backdrop" />}
        <Tabs defaultValue="events" orientation="vertical" onValueChange={(v)=>{ if (v === "pnl" && !pnlData) loadPnl(); if (v === "settled" && !settledData) loadSettled(); }} className="flex min-h-[calc(100vh-56px)]">
          <aside data-testid="admin-sidenav" className={`fixed top-14 left-0 h-[calc(100vh-56px)] w-60 z-40 bg-card border-r border-border overflow-y-auto transition-transform duration-200 ease-out ${navOpen ? "translate-x-0" : "-translate-x-full"}`}>
            <TabsList className="flex flex-col items-stretch gap-1 h-auto p-3 bg-transparent">
              <TabsTrigger value="events" onClick={closeNav} data-testid="tab-events" className="justify-start data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Events</TabsTrigger>
              <TabsTrigger value="users" onClick={closeNav} data-testid="tab-users" className="justify-start data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Users</TabsTrigger>
              <TabsTrigger value="withdrawals" onClick={closeNav} data-testid="tab-withdrawals" className="justify-start data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                Withdrawals
                {withdrawals.filter(w=>w.status==="pending").length > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="withdrawals-badge">
                    {withdrawals.filter(w=>w.status==="pending").length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="pnl" onClick={closeNav} data-testid="tab-pnl" className="justify-start data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">All-time P&amp;L</TabsTrigger>
              <TabsTrigger value="settled" onClick={closeNav} data-testid="tab-settled" className="justify-start data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Settled events</TabsTrigger>
              <TabsTrigger value="rules" onClick={closeNav} data-testid="tab-rules" className="justify-start data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Rules</TabsTrigger>
              <TabsTrigger value="announcements" onClick={closeNav} data-testid="tab-announcements" className="justify-start data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Announcements</TabsTrigger>
              <TabsTrigger value="brand" onClick={closeNav} data-testid="tab-brand" className="justify-start data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Brand</TabsTrigger>
              <TabsTrigger value="settings" onClick={closeNav} data-testid="tab-settings" className="justify-start data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Settings</TabsTrigger>
            </TabsList>
          </aside>
          <div className="flex-1 min-w-0 px-4 md:px-6 py-6 max-w-7xl mx-auto w-full">

          <TabsContent value="events" className="space-y-6">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Plus className="h-4 w-4"/>Create Toss Event</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={createEvent} className="grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="create-event-form">
                  <div><Label>Team 1</Label><Input required value={evForm.team1} onChange={(e)=>setEvForm({...evForm, team1:e.target.value})} data-testid="event-team1" /></div>
                  <div><Label>Team 2</Label><Input required value={evForm.team2} onChange={(e)=>setEvForm({...evForm, team2:e.target.value})} data-testid="event-team2" /></div>
                  <div><Label>Odds</Label><Input required type="number" step="0.01" min="1.01" value={evForm.odds} onChange={(e)=>setEvForm({...evForm, odds:e.target.value})} data-testid="event-odds" /></div>
                  <div><Label>Start</Label><DateTimePicker value={evForm.start_time} onChange={(v)=>setEvForm({...evForm, start_time:v})} testId="event-start" placeholder="Pick start" /></div>
                  <div><Label>End (cutoff)</Label><DateTimePicker value={evForm.end_time} onChange={(v)=>setEvForm({...evForm, end_time:v})} testId="event-end" placeholder="Pick cutoff" /></div>
                  <div className="flex items-end"><Button type="submit" disabled={eventSubmitting} className="w-full btn-push" data-testid="create-event-btn">{eventSubmitting ? "Creating…" : "Create"}</Button></div>
                </form>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {events.filter(e => e.status !== "settled").map((ev) => (
                <Card key={ev.id} data-testid={`event-card-${ev.id}`} className="border-border">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-display">{ev.team1} <span className="text-muted-foreground">vs</span> {ev.team2}</CardTitle>
                      <Badge variant={ev.status === "settled" ? "secondary" : ev.status === "closed" ? "outline" : "default"}>{ev.status.toUpperCase()}</Badge>
                    </div>
                    <CardDescription className="mono text-xs">Odds {ev.odds} · {fmtDT(ev.start_time)} → {fmtDT(ev.end_time)}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg border border-border p-3 bg-card">
                        <div className="text-xs text-muted-foreground">{ev.team1}</div>
                        <div className="mono font-bold text-xl">{fmt(ev.team1_total)}</div>
                        <div className="text-xs text-muted-foreground">{ev.team1_count} bets</div>
                      </div>
                      <div className="rounded-lg border border-border p-3 bg-card">
                        <div className="text-xs text-muted-foreground">{ev.team2}</div>
                        <div className="mono font-bold text-xl">{fmt(ev.team2_total)}</div>
                        <div className="text-xs text-muted-foreground">{ev.team2_count} bets</div>
                      </div>
                    </div>
                    {ev.status === "settled" && (
                      <div className="text-sm">Winner: <b>{ev.winner}</b> · Admin P&L: <span className={`mono ${ev.admin_pnl>=0?"text-green-500":"text-red-500"}`}>{fmt(ev.admin_pnl)}</span></div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={()=>openViewBets(ev)} data-testid={`view-bets-${ev.id}`}>View bets</Button>
                      <Button size="sm" variant="outline" onClick={()=>exportBets(ev.id)} data-testid={`export-${ev.id}`}><Download className="h-3 w-3 mr-1"/>Download .txt</Button>
                      {ev.status !== "settled" && (
                        <>
                          <Button size="sm" variant="outline" onClick={()=>openEdit(ev)} data-testid={`edit-event-${ev.id}`}>Edit</Button>
                          <Button size="sm" onClick={()=>declare(ev.id, "team1")} data-testid={`declare-t1-${ev.id}`}><Trophy className="h-3 w-3 mr-1"/>{ev.team1} won</Button>
                          <Button size="sm" onClick={()=>declare(ev.id, "team2")} data-testid={`declare-t2-${ev.id}`}><Trophy className="h-3 w-3 mr-1"/>{ev.team2} won</Button>
                          <Button size="sm" variant="destructive" onClick={()=>declare(ev.id, "void")} data-testid={`declare-void-${ev.id}`}>Void</Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {events.filter(e => e.status !== "settled").length===0 && <div className="text-muted-foreground">No active events</div>}
            </div>
          </TabsContent>

          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader><CardTitle>Create User</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={createUser} className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="create-user-form">
                  <div><Label>Username</Label><Input required value={userForm.username} onChange={(e)=>setUserForm({...userForm, username:e.target.value})} data-testid="new-user-username" /></div>
                  <div><Label>Password</Label><Input required value={userForm.password} onChange={(e)=>setUserForm({...userForm, password:e.target.value})} data-testid="new-user-password" /></div>
                  <div><Label>Mobile</Label><Input required value={userForm.mobile} onChange={(e)=>setUserForm({...userForm, mobile:e.target.value})} data-testid="new-user-mobile" /></div>
                  <div className="flex items-end"><Button type="submit" className="w-full btn-push" data-testid="create-user-btn">Create</Button></div>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle>Users ({users.length})</CardTitle>
                <Button size="sm" variant="outline" onClick={openBulk} data-testid="bulk-adjust-btn">Bulk adjust coins</Button>
              </CardHeader>
              <CardContent>
                {(() => {
                  const q = userSearch.trim().toLowerCase();
                  const visibleUsers = q
                    ? users.filter((u) =>
                        String(u.username || "").toLowerCase().includes(q) ||
                        String(u.mobile || "").toLowerCase().includes(q)
                      )
                    : users;
                  return (
                    <>
                      <div className="mb-3 flex items-center gap-2">
                        <Input
                          placeholder="Search by username or mobile"
                          value={userSearch}
                          onChange={(e)=>setUserSearch(e.target.value)}
                          className="max-w-sm"
                          data-testid="user-search"
                        />
                        {q && (
                          <>
                            <button
                              type="button"
                              onClick={()=>setUserSearch("")}
                              data-testid="user-search-clear"
                              className="text-xs text-muted-foreground hover:text-foreground underline"
                            >clear</button>
                            <span className="text-xs text-muted-foreground mono" data-testid="user-search-count">
                              {visibleUsers.length} / {users.length}
                            </span>
                          </>
                        )}
                      </div>
                      <Table>
                        <TableHeader><TableRow><TableHead>Username</TableHead><TableHead>Mobile</TableHead><TableHead className="text-right">Balance</TableHead><TableHead className="text-right">Exposure</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {visibleUsers.map((u)=>(
                            <TableRow key={u.id} data-testid={`user-row-${u.username}`}>
                              <TableCell className="font-semibold">{u.username}</TableCell>
                              <TableCell className="mono text-xs">{u.mobile}</TableCell>
                              <TableCell className="text-right mono">{fmt(u.coin_balance)}</TableCell>
                              <TableCell className="text-right mono">{fmt(u.exposure)}</TableCell>
                              <TableCell className="space-x-2">
                                <Button size="sm" variant="outline" onClick={()=>adjustCoins(u)} data-testid={`adjust-${u.username}`}>Adjust coins</Button>
                                <Button size="sm" variant="outline" onClick={()=>openUserDetail(u)} data-testid={`view-${u.username}`}>Details</Button>
                                <Button size="sm" variant="outline" onClick={()=>resetUserPw(u)} data-testid={`reset-user-pw-${u.username}`}><KeyRound className="h-3 w-3 mr-1"/>Reset PW</Button>
                                <Button size="sm" variant="destructive" onClick={()=>{ setDelUser(u); setDelUserConfirm(""); }} data-testid={`delete-user-${u.username}`}><Trash2 className="h-3 w-3 mr-1"/>Delete</Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {visibleUsers.length===0 && (
                            <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                              {users.length===0 ? "No users yet" : "No users match this search"}
                            </TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </>
                  );
                })()}
              </CardContent>
            </Card>

            <Dialog open={!!detailUser} onOpenChange={(o)=>!o&&setDetailUser(null)}>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                {detailUser && (
                  <>
                    <DialogHeader><DialogTitle>{detailUser.user.username} — Details</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="border border-border rounded p-3"><div className="text-xs text-muted-foreground">Balance</div><div className="mono font-bold text-xl">{fmt(detailUser.user.coin_balance)}</div></div>
                      <div className="border border-border rounded p-3"><div className="text-xs text-muted-foreground">Exposure</div><div className="mono font-bold text-xl">{fmt(detailUser.user.exposure)}</div></div>
                    </div>
                    <div>
                      <div className="font-semibold my-2">Coin Statement</div>
                      <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Balance</TableHead><TableHead>Comment</TableHead></TableRow></TableHeader>
                        <TableBody>{detailUser.transactions.map((t)=>(
                          <TableRow key={t.id}><TableCell className="mono text-xs">{fmtDT(t.created_at)}</TableCell><TableCell>{t.type}</TableCell><TableCell className={`mono ${t.amount>=0?"text-green-500":"text-red-500"}`}>{fmt(t.amount)}</TableCell><TableCell className="mono">{fmt(t.balance_after)}</TableCell><TableCell className="text-xs">{t.comment}</TableCell></TableRow>
                        ))}</TableBody></Table>
                    </div>
                    <div>
                      <div className="font-semibold my-2">Bet Statement</div>
                      <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Team</TableHead><TableHead>Amount</TableHead><TableHead>P.Win</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                        <TableBody>{detailUser.bets.map((b)=>(
                          <TableRow key={b.id}><TableCell className="mono text-xs">{fmtDT(b.created_at)}</TableCell><TableCell>{b.team}</TableCell><TableCell className="mono">{fmt(b.amount)}</TableCell><TableCell className="mono">{fmt(b.potential_win)}</TableCell><TableCell>{b.status}</TableCell></TableRow>
                        ))}</TableBody></Table>
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="withdrawals">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Banknote className="h-4 w-4"/>Withdrawal Requests</CardTitle>
                <CardDescription>Approve or reject user withdrawal requests. Rejected requests refund coins to the user&apos;s balance.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow><TableHead>Requested</TableHead><TableHead>User</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Method</TableHead><TableHead>Status</TableHead><TableHead>Resolved</TableHead><TableHead>Comment</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {withdrawals.map((w)=>(
                      <TableRow key={w.id} data-testid={`withdrawal-row-${w.id}`}>
                        <TableCell className="mono text-xs">{fmtDT(w.created_at)}</TableCell>
                        <TableCell className="font-semibold">{w.username}</TableCell>
                        <TableCell className="text-right mono">{fmt(w.amount)}</TableCell>
                        <TableCell><Badge variant="outline" className="uppercase text-[10px]">{w.payment?.method || "—"}</Badge></TableCell>
                        <TableCell><Badge variant={w.status==="approved"?"default":w.status==="rejected"?"destructive":"secondary"}>{w.status}</Badge></TableCell>
                        <TableCell className="mono text-xs">{w.resolved_at ? fmtDT(w.resolved_at) : "—"}</TableCell>
                        <TableCell className="text-xs max-w-[200px] truncate" title={w.comment}>{w.comment || "—"}</TableCell>
                        <TableCell>
                          {w.status === "pending" ? (
                            <Button size="sm" onClick={()=>setResolveModal({ id: w.id, action:"pending", username: w.username, amount: w.amount, comment: "", payment: w.payment, created_at: w.created_at })} data-testid={`review-wd-${w.id}`}>Review</Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={()=>setResolveModal({ id: w.id, action:"view", username: w.username, amount: w.amount, comment: w.comment || "", payment: w.payment, status: w.status, created_at: w.created_at, resolved_at: w.resolved_at })} data-testid={`view-wd-${w.id}`}>Details</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {withdrawals.length===0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">No withdrawal requests yet</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pnl">
            <Card>
              <CardHeader>
                <CardTitle>All-time P&amp;L</CardTitle>
                <CardDescription>Sum across every settled event still on record. Showing the last 31 events for detail.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!pnlData ? (
                  <div className="text-muted-foreground py-6 text-center">Loading…</div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg border border-border p-4 bg-card" data-testid="pnl-total-card">
                        <div className="text-xs text-muted-foreground">Total P&amp;L (all events)</div>
                        <div className={`mono font-bold text-2xl ${pnlData.total_pnl >= 0 ? "text-green-500" : "text-red-500"}`}>
                          {pnlData.total_pnl >= 0 ? "+" : ""}{fmt(pnlData.total_pnl)}
                        </div>
                      </div>
                      <div className="rounded-lg border border-border p-4 bg-card">
                        <div className="text-xs text-muted-foreground">Events settled</div>
                        <div className="mono font-bold text-2xl">{pnlData.total_count}</div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={()=>setPnlListOpen((x)=>!x)}
                      data-testid="pnl-toggle"
                      className="w-full flex items-center justify-between rounded-lg border border-border bg-card p-3 hover:border-primary/50 btn-push"
                    >
                      <span className="font-semibold">Last {Math.min(31, pnlData.events.length)} events</span>
                      <span className="mono text-xs text-muted-foreground">{pnlListOpen ? "▲ collapse" : "▼ expand"}</span>
                    </button>

                    {pnlListOpen && (
                      <Table data-testid="pnl-events-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Settled</TableHead>
                            <TableHead>Match</TableHead>
                            <TableHead>Winner</TableHead>
                            <TableHead className="text-right">P&amp;L</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pnlData.events.map((e) => (
                            <TableRow key={e.id} data-testid={`pnl-row-${e.id}`}>
                              <TableCell className="mono text-xs">{fmtDT(e.settled_at)}</TableCell>
                              <TableCell className="font-semibold">{e.team1} vs {e.team2}</TableCell>
                              <TableCell>
                                <Badge variant={e.winner === "void" ? "secondary" : "default"}>
                                  {e.winner === "team1" ? e.team1 : e.winner === "team2" ? e.team2 : "VOID"}
                                </Badge>
                              </TableCell>
                              <TableCell className={`text-right mono font-semibold ${Number(e.admin_pnl || 0) >= 0 ? "text-green-500" : "text-red-500"}`}>
                                {Number(e.admin_pnl || 0) >= 0 ? "+" : ""}{fmt(e.admin_pnl || 0)}
                              </TableCell>
                              <TableCell><Button size="sm" variant="outline" onClick={()=>openViewBets(e)} data-testid={`pnl-view-bets-${e.id}`}>View bets</Button></TableCell>
                            </TableRow>
                          ))}
                          {pnlData.events.length === 0 && (
                            <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No settled events yet</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settled">
            <Card>
              <CardHeader>
                <CardTitle>Settled events</CardTitle>
                <CardDescription>Winner team and the users who bet on it, with amounts. Tap a row to expand.</CardDescription>
              </CardHeader>
              <CardContent>
                {!settledData ? (
                  <div className="text-muted-foreground py-6 text-center">Loading…</div>
                ) : settledData.length === 0 ? (
                  <div className="text-muted-foreground text-center py-8">No settled events yet</div>
                ) : (
                  <div className="space-y-3">
                    {settledData.map((s) => {
                      const open = !!settledOpen[s.id];
                      const isVoid = s.winner === "void" || !s.winner;
                      return (
                        <div key={s.id} className="rounded-lg border border-border bg-card" data-testid={`settled-row-${s.id}`}>
                          <button
                            type="button"
                            onClick={()=>setSettledOpen((x)=>({ ...x, [s.id]: !x[s.id] }))}
                            data-testid={`settled-toggle-${s.id}`}
                            className="w-full flex items-center justify-between gap-3 p-3 text-left btn-push"
                          >
                            <div className="min-w-0">
                              <div className="font-semibold truncate">{s.team1} <span className="text-muted-foreground">vs</span> {s.team2}</div>
                              <div className="text-xs text-muted-foreground mono">{fmtDT(s.settled_at)} · Odds {s.odds}</div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {isVoid ? (
                                <Badge variant="secondary">VOID</Badge>
                              ) : (
                                <Badge className="bg-green-600" data-testid={`winner-badge-${s.id}`}>Winner · {s.winner_team_name}</Badge>
                              )}
                              <span className="mono text-xs text-muted-foreground">{open ? "▲" : "▼"}</span>
                            </div>
                          </button>

                          {open && (
                            <div className="p-3 border-t border-border">
                              {isVoid ? (
                                <div className="text-sm text-muted-foreground py-2">Event was voided — all bets refunded.</div>
                              ) : (
                                <>
                                  <div className="grid grid-cols-3 gap-2 mb-3">
                                    <div className="rounded border border-border p-2 bg-background">
                                      <div className="text-[10px] text-muted-foreground">Winners</div>
                                      <div className="mono font-bold">{s.winner_count}</div>
                                    </div>
                                    <div className="rounded border border-border p-2 bg-background">
                                      <div className="text-[10px] text-muted-foreground">Total stake</div>
                                      <div className="mono font-bold">{fmt(s.winner_total_stake)}</div>
                                    </div>
                                    <div className="rounded border border-border p-2 bg-background">
                                      <div className="text-[10px] text-muted-foreground">Total payout</div>
                                      <div className="mono font-bold text-green-500">{fmt(s.winner_total_payout)}</div>
                                    </div>
                                  </div>
                                  <Table data-testid={`settled-winners-table-${s.id}`}>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Username</TableHead>
                                        <TableHead className="text-right">Bet amount</TableHead>
                                        <TableHead className="text-right">Payout</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {s.winner_bets.map((b, i) => (
                                        <TableRow key={i} data-testid={`winner-bet-${s.id}-${i}`}>
                                          <TableCell className="font-semibold">{b.username}</TableCell>
                                          <TableCell className="text-right mono">{fmt(b.amount)}</TableCell>
                                          <TableCell className="text-right mono text-green-500">{fmt(b.potential_win)}</TableCell>
                                        </TableRow>
                                      ))}
                                      {s.winner_bets.length === 0 && (
                                        <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-4">No users bet on the winning team</TableCell></TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                </>
                              )}
                              <div className="mt-3 flex justify-between text-xs">
                                <span className="text-muted-foreground">Admin P&amp;L on this event</span>
                                <span className={`mono font-semibold ${s.admin_pnl>=0?"text-green-500":"text-red-500"}`}>
                                  {s.admin_pnl>=0?"+":""}{fmt(s.admin_pnl)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="rules">
            <Card>
              <CardHeader><CardTitle>Book Rules (visible to your users)</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Textarea rows={12} value={rules} onChange={(e)=>setRules(e.target.value)} data-testid="rules-textarea" placeholder="Write your rules here..." />
                <Button onClick={saveRules} className="btn-push" data-testid="save-rules-btn">Save Rules</Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="announcements" className="space-y-6">
            <Card>
              <CardHeader><CardTitle>Post an Announcement</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={createAnnouncement} className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="create-ann-form">
                  <div className="md:col-span-2"><Label>Title</Label><Input required value={annForm.title} onChange={(e)=>setAnnForm({...annForm, title:e.target.value})} data-testid="ann-title"/></div>
                  <div><Label>Start</Label><DateTimePicker value={annForm.start_time} onChange={(v)=>setAnnForm({...annForm, start_time:v})} testId="ann-start" placeholder="Pick start" /></div>
                  <div><Label>End</Label><DateTimePicker value={annForm.end_time} onChange={(v)=>setAnnForm({...annForm, end_time:v})} testId="ann-end" placeholder="Pick end" /></div>
                  <div className="md:col-span-5"><Label>Message</Label><Textarea required value={annForm.message} onChange={(e)=>setAnnForm({...annForm, message:e.target.value})} data-testid="ann-message"/></div>
                  <div className="md:col-span-5"><Button type="submit" className="btn-push" data-testid="ann-submit">Post</Button></div>
                </form>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Announcements</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {announcements.map((a)=>(
                  <div key={a.id} className="border border-border rounded-lg p-3 flex justify-between items-start" data-testid={`ann-${a.id}`}>
                    <div>
                      <div className="font-semibold">{a.title}</div>
                      <div className="text-sm text-muted-foreground">{a.message}</div>
                      <div className="text-xs mono text-muted-foreground mt-1">{fmtDT(a.start_time)} → {fmtDT(a.end_time)}</div>
                    </div>
                    <Button size="sm" variant="destructive" onClick={()=>delAnnouncement(a.id)}>Delete</Button>
                  </div>
                ))}
                {announcements.length===0 && <div className="text-muted-foreground">No announcements</div>}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="brand">
            <Card>
              <CardHeader><CardTitle>Brand Settings</CardTitle><CardDescription>Shown to your users on their login page.</CardDescription></CardHeader>
              <CardContent className="space-y-3 max-w-lg">
                <div><Label>Brand name</Label><Input value={brand.brand_name} onChange={(e)=>setBrand({...brand, brand_name:e.target.value})} data-testid="brand-name-input"/></div>
                <div><Label>Logo URL</Label><Input value={brand.brand_logo_url} onChange={(e)=>setBrand({...brand, brand_logo_url:e.target.value})} placeholder="https://..." data-testid="brand-logo-input"/></div>
                {brand.brand_logo_url && <img src={brand.brand_logo_url} alt="preview" className="h-20 w-20 rounded-lg object-cover border border-border"/>}
                <div className="pt-3 mt-2 border-t border-border">
                  <div className="text-sm font-semibold">Player support links</div>
                  <div className="text-xs text-muted-foreground">Displayed as floating icons on the bottom-right of your users&apos; interface.</div>
                </div>
                <div><Label>WhatsApp support URL</Label><Input value={brand.support_whatsapp || ""} onChange={(e)=>setBrand({...brand, support_whatsapp:e.target.value})} placeholder="https://wa.me/91XXXXXXXXXX" data-testid="brand-whatsapp-input"/></div>
                <div><Label>Telegram support URL</Label><Input value={brand.support_telegram || ""} onChange={(e)=>setBrand({...brand, support_telegram:e.target.value})} placeholder="https://t.me/yourbrand" data-testid="brand-telegram-input"/></div>
                <Button onClick={saveBrand} className="btn-push" data-testid="save-brand-btn">Save</Button>
                <div className="text-xs mono text-muted-foreground pt-3">Your brand code: <b>{user?.brand_slug}</b> · User login link: {userLoginLink}</div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Access Code</CardTitle>
                <CardDescription>Required at login alongside username & password. Refresh weekly (or now).</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="border border-border rounded px-3 py-2 mono text-lg" data-testid="admin-code">
                    {loginCode.visible ? loginCode.code : "••••••••"}
                  </div>
                  <Button size="sm" variant="ghost" onClick={()=>setLoginCode({...loginCode, visible:!loginCode.visible})} data-testid="admin-code-toggle">
                    {loginCode.visible ? "Hide" : "Show"}
                  </Button>
                  <div className="text-xs text-muted-foreground">Last updated {loginCode.rotated_at ? new Date(loginCode.rotated_at).toLocaleString() : "—"}</div>
                </div>
                <Button onClick={rotateCode} className="btn-push" data-testid="admin-rotate-code">Rotate now</Button>
              </CardContent>
            </Card>
          </TabsContent>
          </div>
        </Tabs>
      </main>

      {/* View Bets dialog */}
      <Dialog open={!!viewBets} onOpenChange={(o)=>!o&&setViewBets(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="view-bets-dialog">
          {viewBets && (
            <>
              <DialogHeader><DialogTitle>Bets — {viewBets.event.team1} vs {viewBets.event.team2}</DialogTitle></DialogHeader>
              {["team1","team2"].map((tk) => {
                const teamName = tk === "team1" ? viewBets.event.team1 : viewBets.event.team2;
                const rows = viewBets.bets.filter(b => b.team === tk);
                const total = rows.reduce((s,b)=>s+Number(b.amount||0),0);
                return (
                  <div key={tk} className="mb-4">
                    <div className="font-semibold my-2">{teamName} — {rows.length} bets · total {fmt(total)}</div>
                    <Table>
                      <TableHeader><TableRow><TableHead>Username</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Potential Win</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {rows.map((b)=>(
                          <TableRow key={b.id}><TableCell className="font-semibold">{b.username}</TableCell><TableCell className="text-right mono">{fmt(b.amount)}</TableCell><TableCell className="text-right mono">{fmt(b.potential_win)}</TableCell><TableCell>{b.status}</TableCell></TableRow>
                        ))}
                        {rows.length===0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-4">No bets on this side</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </div>
                );
              })}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Event dialog */}
      <Dialog open={!!editEvent} onOpenChange={(o)=>!o&&setEditEvent(null)}>
        <DialogContent data-testid="edit-event-dialog">
          {editEvent && (
            <>
              <DialogHeader><DialogTitle>Edit event</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Team 1</Label><Input value={editEvent.team1} onChange={(e)=>setEditEvent({...editEvent, team1:e.target.value})} data-testid="edit-team1"/></div>
                <div><Label>Team 2</Label><Input value={editEvent.team2} onChange={(e)=>setEditEvent({...editEvent, team2:e.target.value})} data-testid="edit-team2"/></div>
                <div><Label>Odds</Label><Input type="number" step="0.01" min="1.01" value={editEvent.odds} onChange={(e)=>setEditEvent({...editEvent, odds:e.target.value})} data-testid="edit-odds"/></div>
                <div></div>
                <div><Label>Start</Label><DateTimePicker value={editEvent.start_time} onChange={(v)=>setEditEvent({...editEvent, start_time:v})} testId="edit-start" placeholder="Pick start" /></div>
                <div><Label>End (cutoff)</Label><DateTimePicker value={editEvent.end_time} onChange={(v)=>setEditEvent({...editEvent, end_time:v})} testId="edit-end" placeholder="Pick cutoff" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer">
                <input type="checkbox" checked={editEvent.void_bets} onChange={(e)=>setEditEvent({...editEvent, void_bets:e.target.checked})} data-testid="edit-void-bets"/>
                Void &amp; refund all existing active bets on this event
              </label>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setEditEvent(null)}>Cancel</Button>
                <Button onClick={saveEdit} data-testid="edit-save-btn">Save changes</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Declare winner preview dialog */}
      <Dialog open={!!declarePreview} onOpenChange={(o)=>!o&&setDeclarePreview(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="declare-preview-dialog">
          {declarePreview && (
            <>
              <DialogHeader><DialogTitle>Confirm result: {declarePreview.winner === "void" ? "VOID (refund all)" : `Winner: ${declarePreview.winner === "team1" ? declarePreview.team1 : declarePreview.team2}`}</DialogTitle></DialogHeader>
              <div className="text-sm text-muted-foreground">{declarePreview.count} active bets · Admin P&amp;L: <span className={`mono font-bold ${declarePreview.admin_pnl>=0?"text-green-500":"text-red-500"}`}>{fmt(declarePreview.admin_pnl)}</span></div>
              {declarePreview.winner !== "void" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <div>
                    <div className="font-semibold mb-1 text-green-500">Winning ({declarePreview.winners.length})</div>
                    <Table><TableHeader><TableRow><TableHead>User</TableHead><TableHead className="text-right">Stake</TableHead><TableHead className="text-right">Payout</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {declarePreview.winners.map((w,i)=>(<TableRow key={i}><TableCell>{w.username}</TableCell><TableCell className="text-right mono">{fmt(w.amount)}</TableCell><TableCell className="text-right mono text-green-500">+{fmt(w.payout)}</TableCell></TableRow>))}
                        {declarePreview.winners.length===0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-3">None</TableCell></TableRow>}
                      </TableBody></Table>
                  </div>
                  <div>
                    <div className="font-semibold mb-1 text-red-500">Losing ({declarePreview.losers.length})</div>
                    <Table><TableHeader><TableRow><TableHead>User</TableHead><TableHead className="text-right">Stake</TableHead><TableHead className="text-right">Loss</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {declarePreview.losers.map((l,i)=>(<TableRow key={i}><TableCell>{l.username}</TableCell><TableCell className="text-right mono">{fmt(l.amount)}</TableCell><TableCell className="text-right mono text-red-500">-{fmt(l.loss)}</TableCell></TableRow>))}
                        {declarePreview.losers.length===0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-3">None</TableCell></TableRow>}
                      </TableBody></Table>
                  </div>
                </div>
              )}
              {declarePreview.winner === "void" && (
                <div className="mt-3">
                  <div className="font-semibold mb-1">Refunds ({declarePreview.voids.length})</div>
                  <Table><TableHeader><TableRow><TableHead>User</TableHead><TableHead>Team</TableHead><TableHead className="text-right">Refund</TableHead></TableRow></TableHeader>
                    <TableBody>{declarePreview.voids.map((v,i)=>(<TableRow key={i}><TableCell>{v.username}</TableCell><TableCell>{v.team}</TableCell><TableCell className="text-right mono">{fmt(v.refund)}</TableCell></TableRow>))}</TableBody></Table>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={()=>setDeclarePreview(null)}>Cancel</Button>
                <Button onClick={confirmDeclare} disabled={declareSubmitting} className="btn-push" data-testid="declare-confirm-btn">{declareSubmitting ? "Settling…" : "Confirm & settle"}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Delete user dialog */}
      <Dialog open={!!delUser} onOpenChange={(o)=>{ if(!o){ setDelUser(null); setDelUserConfirm(""); } }}>
        <DialogContent data-testid="delete-user-dialog">
          {delUser && (
            <>
              <DialogHeader><DialogTitle>Delete user · {delUser.username}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500">
                  This will permanently delete the user and <b>all their bets, transactions, and withdrawal history</b>. Any current coin balance will be forfeited. This action cannot be undone.
                </div>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Username</span><span className="font-semibold">{delUser.username}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Mobile</span><span className="mono">{delUser.mobile}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Balance</span><span className="mono">{fmt(delUser.coin_balance)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Exposure</span><span className="mono">{fmt(delUser.exposure)}</span></div>
                </div>
                <div>
                  <Label>Type <span className="mono font-bold">{delUser.username}</span> to confirm</Label>
                  <Input value={delUserConfirm} onChange={(e)=>setDelUserConfirm(e.target.value)} data-testid="delete-user-confirm-input" autoFocus />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>{ setDelUser(null); setDelUserConfirm(""); }} data-testid="delete-user-cancel">Cancel</Button>
                <Button variant="destructive" onClick={deleteUser} disabled={delUserConfirm.trim() !== delUser.username} data-testid="delete-user-confirm">Delete permanently</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk adjust coins dialog */}
      <Dialog open={!!bulkModal} onOpenChange={(o)=>!o&&setBulkModal(null)}>
        <DialogContent data-testid="bulk-adjust-dialog">
          {bulkModal && (
            <>
              <DialogHeader><DialogTitle>Bulk adjust coins · {users.length} users</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="rounded-lg border border-orange-500/40 bg-orange-500/10 p-3 text-xs text-orange-500">
                  This action applies the same amount to every active user under your brand. Debits automatically skip users with insufficient balance.
                </div>
                <div>
                  <Label>Action</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <button type="button" onClick={()=>setBulkModal({...bulkModal, direction:"credit"})}
                      data-testid="bulk-credit"
                      className={`rounded-lg border px-3 py-2 text-sm font-semibold btn-push ${bulkModal.direction==="credit"?"border-green-500 bg-green-500/10 text-green-500":"border-border text-muted-foreground"}`}>
                      + Credit all users
                    </button>
                    <button type="button" onClick={()=>setBulkModal({...bulkModal, direction:"debit"})}
                      data-testid="bulk-debit"
                      className={`rounded-lg border px-3 py-2 text-sm font-semibold btn-push ${bulkModal.direction==="debit"?"border-red-500 bg-red-500/10 text-red-500":"border-border text-muted-foreground"}`}>
                      − Debit all users
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Amount per user</Label>
                  <Input type="number" min="1" step="1" value={bulkModal.amount}
                    onChange={(e)=>setBulkModal({...bulkModal, amount:e.target.value})}
                    placeholder="e.g. 100" data-testid="bulk-amount" autoFocus />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {[100, 500, 1000, 5000].map((v)=>(
                      <button key={v} type="button" onClick={()=>{
                        const cur = parseFloat(bulkModal.amount) || 0;
                        setBulkModal({...bulkModal, amount: String(cur + v)});
                      }} data-testid={`bulk-preset-${v}`}
                        className="px-3 py-1 rounded-full border border-border text-xs font-semibold mono bg-card hover:border-primary hover:bg-primary/10 btn-push">+{v}</button>
                    ))}
                    <button type="button" onClick={()=>setBulkModal({...bulkModal, amount:""})} data-testid="bulk-clear"
                      className="px-3 py-1 rounded-full border border-border text-xs font-semibold text-muted-foreground bg-card hover:border-red-500 hover:text-red-500 btn-push">Clear</button>
                  </div>
                </div>
                <div>
                  <Label>Comment (visible in every user&apos;s account statement)</Label>
                  <Textarea rows={2} value={bulkModal.comment}
                    onChange={(e)=>setBulkModal({...bulkModal, comment:e.target.value})}
                    placeholder="e.g. Weekend bonus, Diwali gift" data-testid="bulk-comment" />
                </div>
                {bulkModal.amount && parseFloat(bulkModal.amount) > 0 && (
                  <div className="rounded-lg border border-border p-3 bg-card text-sm flex justify-between">
                    <span className="text-muted-foreground">Total impact ({users.length} users)</span>
                    <span className={`mono font-bold ${bulkModal.direction==="credit"?"text-green-500":"text-red-500"}`}>
                      {bulkModal.direction==="credit" ? "+" : "−"}{fmt(parseFloat(bulkModal.amount) * users.length)}
                    </span>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setBulkModal(null)} data-testid="bulk-cancel">Cancel</Button>
                <Button onClick={submitBulk}
                  variant={bulkModal.direction==="debit"?"destructive":"default"}
                  className="btn-push" data-testid="bulk-confirm">
                  {bulkModal.direction==="credit" ? "Credit all" : "Debit all"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust coins dialog */}
      <Dialog open={!!adjustModal} onOpenChange={(o)=>!o&&setAdjustModal(null)}>
        <DialogContent data-testid="adjust-coins-dialog">
          {adjustModal && (
            <>
              <DialogHeader><DialogTitle>Adjust coins · {adjustModal.user.username}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="rounded-lg border border-border p-3 bg-card text-sm flex justify-between">
                  <span className="text-muted-foreground">Current balance</span>
                  <span className="mono font-bold">{fmt(adjustModal.user.coin_balance)}</span>
                </div>

                <div>
                  <Label>Action</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <button type="button" onClick={()=>setAdjustModal({...adjustModal, direction:"credit"})}
                      data-testid="adjust-credit"
                      className={`rounded-lg border px-3 py-2 text-sm font-semibold btn-push ${adjustModal.direction==="credit"?"border-green-500 bg-green-500/10 text-green-500":"border-border text-muted-foreground"}`}>
                      + Increase (Credit)
                    </button>
                    <button type="button" onClick={()=>setAdjustModal({...adjustModal, direction:"debit"})}
                      data-testid="adjust-debit"
                      className={`rounded-lg border px-3 py-2 text-sm font-semibold btn-push ${adjustModal.direction==="debit"?"border-red-500 bg-red-500/10 text-red-500":"border-border text-muted-foreground"}`}>
                      − Decrease (Debit)
                    </button>
                  </div>
                </div>

                <div>
                  <Label>Amount</Label>
                  <Input type="number" min="1" step="1" value={adjustModal.amount}
                    onChange={(e)=>setAdjustModal({...adjustModal, amount:e.target.value})}
                    placeholder="e.g. 100" data-testid="adjust-amount" autoFocus />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {[100, 500, 1000, 5000, 10000].map((v)=>(
                      <button key={v} type="button" onClick={()=>{
                        const cur = parseFloat(adjustModal.amount) || 0;
                        setAdjustModal({...adjustModal, amount: String(cur + v)});
                      }} data-testid={`adjust-preset-${v}`}
                        className="px-3 py-1 rounded-full border border-border text-xs font-semibold mono bg-card hover:border-primary hover:bg-primary/10 btn-push">+{v}</button>
                    ))}
                    <button type="button" onClick={()=>setAdjustModal({...adjustModal, amount:""})} data-testid="adjust-clear"
                      className="px-3 py-1 rounded-full border border-border text-xs font-semibold text-muted-foreground bg-card hover:border-red-500 hover:text-red-500 btn-push">Clear</button>
                  </div>
                </div>

                <div>
                  <Label>Comment (visible in user account statement)</Label>
                  <Textarea rows={2} value={adjustModal.comment}
                    onChange={(e)=>setAdjustModal({...adjustModal, comment:e.target.value})}
                    placeholder="e.g. Bonus, Cash top-up, Refund" data-testid="adjust-comment" />
                </div>

                {adjustModal.amount && parseFloat(adjustModal.amount) > 0 && (
                  <div className="rounded-lg border border-border p-3 bg-card text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">New balance</span>
                      <span className={`mono font-bold ${adjustModal.direction==="credit"?"text-green-500":"text-red-500"}`}>
                        {fmt(Number(adjustModal.user.coin_balance || 0) + (adjustModal.direction==="credit" ? 1 : -1) * parseFloat(adjustModal.amount || 0))}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setAdjustModal(null)} data-testid="adjust-cancel">Cancel</Button>
                <Button onClick={submitAdjust}
                  variant={adjustModal.direction==="debit"?"destructive":"default"}
                  className="btn-push" data-testid="adjust-confirm">
                  {adjustModal.direction==="credit" ? "Credit coins" : "Debit coins"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Review withdrawal dialog */}
      <Dialog open={!!resolveModal} onOpenChange={(o)=>!o&&setResolveModal(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="resolve-wd-dialog">
          {resolveModal && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {resolveModal.action === "view" ? "Withdrawal details" : "Review withdrawal request"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="rounded-lg border border-border p-3 bg-card text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">User</span><span className="font-semibold">{resolveModal.username}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="mono font-bold text-lg">{fmt(resolveModal.amount)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Requested</span><span className="mono text-xs">{fmtDT(resolveModal.created_at)}</span></div>
                  {resolveModal.action === "view" && (
                    <>
                      <div className="flex justify-between"><span className="text-muted-foreground">Status</span>
                        <Badge variant={resolveModal.status==="approved"?"default":resolveModal.status==="rejected"?"destructive":"secondary"}>{resolveModal.status}</Badge>
                      </div>
                      {resolveModal.resolved_at && (
                        <div className="flex justify-between"><span className="text-muted-foreground">Resolved</span><span className="mono text-xs">{fmtDT(resolveModal.resolved_at)}</span></div>
                      )}
                    </>
                  )}
                </div>

                <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm space-y-1" data-testid="wd-payment-details">
                  <div className="font-semibold text-primary mb-1">Payment details from user</div>
                  {resolveModal.payment ? (
                    <>
                      <div className="flex justify-between"><span className="text-muted-foreground">Method</span><span className="font-semibold uppercase">{resolveModal.payment.method || "—"}</span></div>
                      {resolveModal.payment.method === "upi" && (
                        <div className="flex justify-between"><span className="text-muted-foreground">UPI ID</span><span className="mono break-all">{resolveModal.payment.upi_id || "—"}</span></div>
                      )}
                      {resolveModal.payment.method === "bank" && (
                        <>
                          <div className="flex justify-between"><span className="text-muted-foreground">Account holder</span><span>{resolveModal.payment.account_holder || "—"}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Account number</span><span className="mono">{resolveModal.payment.account_number || "—"}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">IFSC</span><span className="mono">{resolveModal.payment.ifsc || "—"}</span></div>
                          {resolveModal.payment.bank_name && <div className="flex justify-between"><span className="text-muted-foreground">Bank</span><span>{resolveModal.payment.bank_name}</span></div>}
                        </>
                      )}
                      {resolveModal.payment.notes && (
                        <div className="pt-1 border-t border-primary/20 mt-2">
                          <div className="text-muted-foreground text-xs">User note</div>
                          <div className="text-sm break-words">{resolveModal.payment.notes}</div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-xs text-muted-foreground">No payment details on file (legacy request).</div>
                  )}
                </div>

                {resolveModal.action === "pending" ? (
                  <div>
                    <Label>Admin comment (required, shown to user)</Label>
                    <Textarea rows={3} value={resolveModal.comment}
                      onChange={(e)=>setResolveModal({...resolveModal, comment:e.target.value})}
                      data-testid="resolve-wd-comment"
                      placeholder="e.g. Paid via UPI ref #12345  /  Please retry with correct IFSC" />
                    <p className="text-xs text-muted-foreground mt-2">
                      <b>Approve</b>: locked coins are permanently deducted from the user.<br/>
                      <b>Reject</b>: locked coins are refunded back to the user&apos;s balance.
                    </p>
                  </div>
                ) : (
                  resolveModal.comment && (
                    <div>
                      <Label>Admin comment</Label>
                      <div className="text-sm mt-1 p-2 rounded border border-border bg-card">{resolveModal.comment}</div>
                    </div>
                  )
                )}
              </div>
              <DialogFooter className="gap-2 sm:justify-between">
                <Button variant="outline" onClick={()=>setResolveModal(null)} data-testid="resolve-wd-close">Close</Button>
                {resolveModal.action === "pending" && (
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      onClick={()=>resolveWithdrawal("reject")}
                      data-testid="resolve-wd-reject">
                      Reject
                    </Button>
                    <Button
                      onClick={()=>resolveWithdrawal("approve")}
                      className="btn-push" data-testid="resolve-wd-approve">
                      Approve
                    </Button>
                  </div>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
